<script>
import { mapMutations, mapGetters } from 'vuex'

export default {
  name: "CartItem",
  props: {
    cartItem: Object,
    index: Number,
    canMutateStore: { type: Boolean, default: false }, // может ли обновлять свое состояние во Vuex
                                                       // только один экземпляр, соответствующий элементу корзины,
                                                       // должен иметь этот атрибут
  },
  watch: {
    minNumber: {
      handler() {
        if (!this.canMutateStore) {
          return
        }
        if (this.number < this.minNumber) {
          this.updateNumber(this.minNumber)
        }
      },
      immediate: true,
    },
  },
  created() {
    if (!this.canMutateStore) {
      return
    }
    this.initDefaultColor()
    this.initDefaultSize()
    this.initDefaultDensity()
  },
  data() {
    return {
      showPrintVisual: false,
      highlightedLocation: '',
      showErrors: false,
      errors: {
        zeroPrintLocations: 'Добавьте нанесение',
        insufficientNumber: '',
      },
    }
  },
  computed: {
    ...mapGetters([
      'emptyPrintLocations',
    ]),
    isBag() {
      if (!this.cartItem.type) return
      return this.cartItem.type.id === 'bags'
    },
    textileSizes() {
      if (!this.isBag) return
      return this.$store.state.textileSizes.bags
    },
    number() {
      if (!this.isBag) {
        return this.cartItem.number
      }
      return this.cartItem.sizes.reduce((sum, size) => (sum + size.number), 0)
    },
    textileDensities() {
      if (!this.isBag) return
      return this.$store.state.density.bags
    },
    textileColors() {
      return !this.isBag ? this.$store.state.textileColors : this.$store.state.bagColors
    },
    isColored() {
      return this.cartItem.color.id !== 'white'
    },
    minNumber() {
      if (!this.cartItem.model) {
        return 0
      }
      if (this.isBag) {
        return 100
      }
      if (this.cartItem.printLocations.length) {
        return 1
      }

      // на основании выбранных технологий печати
      const printTechnologyRequirements = this.cartItem.printLocations.map((location) => {
        return location.technology.minQuantity
      })

      return Math.max(
        ...[
          Math.ceil(this.cartItem.model.minOrderAmount / this.prices.current), // на основании мин. возможной суммы заказа
          ...printTechnologyRequirements,
        ]
      )
    },
    existingPrices() {
      if (!this.cartItem.model) {
        return
      }
      return this.cartItem.model.priceList.map(range => range.price)
    },
    maxPrice() {
      if (!this.cartItem.model) {
        return
      }
      return Math.max.apply(null, this.existingPrices)
    },
    /**
     * Подходящие диапазоны тиража
     */
    matchingQuantityRanges() {
      if (!this.cartItem.model) {
        return
      }
      return this.cartItem.model.priceList.filter((range, index, priceList) => {
        const nextRange = priceList[index + 1]
        if (nextRange) {
          return (this.number >= range.from && this.number < nextRange.from)
        }
        return this.number >= range.from
      })
    },
    matchingQuantityRange() {
      if (!this.cartItem.model) {
        return
      }
      return this.matchingQuantityRanges[0]
    },
    nextQuantityRange() {
      if (!this.cartItem.model) {
        return
      }
      const currentRangeIndex = this.cartItem.model.priceList.indexOf(this.matchingQuantityRange)
      return this.cartItem.model.priceList[currentRangeIndex + 1]
    },
    resultQuantityRange() {
      if (!this.cartItem.model) {
        return
      }
      if (!this.nextQuantityRange) {
        return this.matchingQuantityRange
      } else {
        return (this.matchingQuantityRange.price * this.cartItem.number) <= (this.nextQuantityRange.price * this.nextQuantityRange.from)
          ? this.matchingQuantityRange
          : this.nextQuantityRange
      }
    },
    colorPriceFactor() {
      return (this.isBag && this.isColored) ? 3 : 0
    },
    densityPriceFactor() {
      if (!this.isBag) return 0
      return this.cartItem.density.priceIncrease
    },
    prices() {
      if (!this.cartItem.model) {
        return
      }
      return {
        max: this.maxPrice,
        current: this.resultQuantityRange.price
      }
    },
    resultPrice() {
      if (!this.cartItem.model) {
        return
      }
      if (this.isBag) {
        return 0
      }
      return this.prices.current < this.prices.max
        ? this.prices.current
        : this.prices.max
    },
    totals() {
      if (!this.cartItem.model || !this.cartItem.type) {
        return
      }
      if (!this.isBag) {
        return {
          max: this.prices.max * this.number,
          current: this.resultPrice * this.number,
          print: this.cartItem.printLocations.reduce((sum, location) => {
            return sum + location.cost * this.number
          }, 0) + this.totalPrintInitialExpences,
          packing: this.cartItem.pack ? this.number * this.cartItem.type.packingPrice : 0
        }
      } else {
        return {
          max: 0,
          current: this.cartItem.sizes.reduce((sum, size) => {
            const generalPrice = this.prices.current + this.colorPriceFactor + this.densityPriceFactor
            return sum + (generalPrice + size.data.priceIncrease) * size.number
          }, 0),
          print: this.bagPrintPrice * this.number,
          packing: 0
        }
      }
    },
    total() {
      if (!this.cartItem.model) {
        return
      }
      return this.totals.current + this.totals.print + this.totals.packing
    },
    totalPerItem() {
      return Math.ceil(this.total / this.number)
    },
    appPrintLocations() {
      return this.$store.state.textilePrint.locations
    },
    // доступные для данного вида текстиля места нанесения - список названий
    textileTypePrintLocationsList() {
      if (!this.cartItem.type) {
        return
      }
      return this.cartItem.type.printLocations
    },
    // доступные для данного вида текстиля места нанесения в виде списка объектов
    availablePrintLocations() {
      if (!this.textileTypePrintLocationsList) {
        return []
      }
      return this.textileTypePrintLocationsList.map(locationName => {
        return Object.assign({}, this.appPrintLocations[locationName])
      })
    },
    silkScreenInitialExpences() {
      const silkScreenPrintLocations = this.cartItem.printLocations.filter(location => location.technology.id === 'silkScreen')
      if (!silkScreenPrintLocations.length) {
        return 0
      }
      return silkScreenPrintLocations.reduce((sum, location) => {
        return sum + location.calculations.totalSelectedColorsNumber * this.$store.state.textilePrint.technologies.silkScreen.printFormPrice
      }, 0)
    },
    fullColorInitialExpences() {
      const fullColorPrintLocations = this.cartItem.printLocations.filter(location => location.technology.id === 'fullColor')
      if (!fullColorPrintLocations.length) {
        return 0
      }
      return fullColorPrintLocations.length * this.$store.state.textilePrint.technologies.fullColor.initialExpences
    },
    embroideryInitialExpences() {
      const embroideryPrintLocations = this.cartItem.printLocations.filter(location => location.technology.id === 'embroidery')
      if (!embroideryPrintLocations.length) {
        return 0
      }
      return embroideryPrintLocations.length * this.$store.state.textilePrint.technologies.embroidery.initialExpences
    },
    totalPrintInitialExpences() {
      return this.silkScreenInitialExpences + this.fullColorInitialExpences + this.embroideryInitialExpences
    },
    itemColorMatchingSlide() {
      if (!this.cartItem.model) {
        return
      }
      const matchingSlides = this.cartItem.model.slides.filter((slide) => slide.color === this.color, this)
      return matchingSlides.length
        ? matchingSlides[0]
        : this.cartItem.model.slides.filter((slide) => slide.color === this.cartItem.model.defaultColor, this)[0]
    },
    thumb() {
      if (!this.cartItem.model) {
        return
      }
      return this.itemColorMatchingSlide.thumb
    },
    /**
     * === СПЕЦИФИЧНО ДЛЯ ПАКЕТОВ ===
     */
    /**
     * Общее количество цветов (включая подложки) на всех местах нанесения
     */
    bagPrintTechnology() {
      return this.$store.state.textilePrint.technologies.bagPrint
    },
    bagTotalSelectedPrintColorsNumber() {
      if (!this.isBag) return
      return this.cartItem.printLocations.reduce((sum, location) => {
        return sum + location.calculations.totalSelectedColorsNumber
      }, 0)
    },
    printLocationsWithErrors() {
      return this.emptyPrintLocations
    },
    errorsList() {
      let errors = []
      if (this.isBag && this.cartItem.printLocations.length === 0) {
        errors.push('zeroPrintLocations')
      }
      if (this.number < this.minNumber) {
        errors.push('insufficientNumber')
      }
      return errors
    },
    hasErrors() {
      return this.errorsList.length > 0 || this.printLocationsWithErrors.length > 0
    },
    /**
     * Общий мультипликатор цены печати всех мест нанесения, если количество общее количество цветов для печати меньше 4
     */
    bagConsolidatePrintPriceMultiplier() {
      if (this.bagTotalSelectedPrintColorsNumber > 4) return
      return this.cartItem.printLocations.reduce((sum, location) => {
        return sum + location.calculations.totalPrintPriceMultiplier
                       * location.calculations.totalSelectedColorsNumber
                       / this.bagTotalSelectedPrintColorsNumber
      }, 0)
    },
    bagPrintPrice() {
      if (!this.isBag) return
      if (!this.bagTotalSelectedPrintColorsNumber) return 0
      if (this.bagTotalSelectedPrintColorsNumber <= 4) {
        const priceRange = this.bagPrintTechnology.priceList.filter((range, index, ranges) => {
          const nextRange = ranges[index + 1]
          if (nextRange) {
            return this.number >= range.from && this.number < nextRange.from
          }
          return true
        })[0]
        return Math.ceil(priceRange.colorsNumber[`is${this.bagTotalSelectedPrintColorsNumber}`] * this.bagConsolidatePrintPriceMultiplier)
      } else {
        return this.cartItem.printLocations.reduce((sum, location) => {
          return sum + location.cost
        }, 0)
      }
    },
  },
  methods: {
    ...mapMutations([
      'addCartItem',
      'updateCartItem',
      'restoreCartItem',
      'removeCartItem',
    ]),
    /**
     * Сообщает о готовности к сохранению редактируемого элемента корзины
     */
    save() {
      if (this.hasErrors) {
        this.showErrors = true
        return
      }
      /**
       * Редактируемый товар готов к сохранению
       */
      this.$emit('save')
    },
    update(propsToUpdate, item = this.cartItem) {
      this.updateCartItem({ item, propsToUpdate })
      this.$nextTick(() => {
        this.updateCartItem({
          item: this.cartItem.calculations,
          propsToUpdate: {
            totals: this.totals,
            total: this.total,
          }
        })
      })
    },
    updateNumber(val) {
      this.$nextTick(() => {
        if (!this.isBag) {
          this.update({ number: val })
        }
        this.$nextTick(() => {
          this.update({ prices: this.prices })
        })
      })
    },
    updateComponentProp(prop, val) {
      this[prop] = val
    },
    onNumberFieldInput(number) {
      this.updateNumber(number)
    },
    onTypeReset() {
      if (!this.cartItemBackup) {
        this.backupCartItem()
      }
      this.update({ model: null })
      this.update({ type: null })
      this.resetItemSpecificProps()
    },
    onModelReset() {
      if (!this.cartItemBackup) {
        this.backupCartItem()
      }
      this.update({ model: null })
      this.resetItemSpecificProps()
    },
    onModelReplace({ type, model }) {
      if (this.cartItemBackup && this.cartItemBackup.model && (this.cartItemBackup.model.model === model.model)) {
        this.restoreCartItem(this.index)
        return
      }
      this.addCartItem({ type, model, index: this.index })
    },
    backupCartItem() {
      const backup = !this.isBag
        ? {
          type: this.cartItem.type,
          model: this.cartItem.model,
          color: this.cartItem.color,
          number: this.cartItem.number,
          printLocations: this.cartItem.printLocations,
          pack: this.cartItem.pack,
        }
        : {
          type: this.cartItem.type,
          model: this.cartItem.model,
          color: this.cartItem.color,
          sizes: this.cartItem.sizes,
          density: this.cartItem.density,
          printLocations: this.cartItem.printLocations,
        }
      this.updateCartItem({
        item: this.cartItem,
        propsToUpdate: { backup }
      })
      this.showPrintVisual = false
    },
    resetItemSpecificProps() {
      const props = !this.isBag
        ? {
          color: null,
          printLocations: [],
          number: 0,
          prices: {
            current: 0,
            max: 0,
          },
          pack: false,
          calculations: {
            totals: null,
            total: null,
          },
        }
        : {
          color: null,
          printLocations: [],
          prices: {
            current: 0,
            max: 0,
          },
          sizes: [],
          density: null,
          calculations: {
            totals: null,
            total: null,
          },
        }
      this.update(props)
    },
    initDefaultColor() {
      if (this.cartItem.color) return
      this.update({ color: this.textileColors[this.cartItem.model.defaultColor] })
    },
    initDefaultSize() {
      if (!this.isBag || this.cartItem.size) return
      this.update({ size: this.textileSizes['bag40x50'] })
    },
    initDefaultDensity() {
      if (!this.isBag || this.cartItem.density) return
      this.update({ density: this.textileDensities[0] })
    },
    setHighlightedLocation(val) {
      this.highlightedLocation = val
    },
    onSliderReady() {
      if (!this.isBag) return
      this.showPrintVisual = true
    },
  },
  render() {
    return this.$scopedSlots.default({
      cartItem: this.cartItem,
      cartItemIndex: this.index,

      showPrintVisual: this.showPrintVisual,
      highlightedLocation: this.highlightedLocation,
      showErrors: this.showErrors,
      errors: this.errors,

      availablePrintLocations: this.availablePrintLocations,
      textileDensities: this.textileDensities,
      textileSizes: this.textileSizes,
      number: this.number,
      minNumber: this.minNumber,
      prices: this.prices,
      resultPrice: this.resultPrice,
      totals: this.totals,
      total: this.total,
      totalPerItem: this.totalPerItem,
      thumb: this.thumb,
      isBag: this.isBag,
      errorsList: this.errorsList,
      hasErrors: this.hasErrors,

      save: this.save,
      update: this.update,
      updateComponentProp: this.updateComponentProp,
      removeCartItem: this.removeCartItem,
      onNumberFieldInput: this.onNumberFieldInput,
      onTypeReset: this.onTypeReset,
      onModelReset: this.onModelReset,
      onModelReplace: this.onModelReplace,
      setHighlightedLocation: this.setHighlightedLocation,
      onSliderReady: this.onSliderReady,
    })
  },
}
</script>